import React from 'react';
import type { RecurrenceKind } from 'src/types/Schedules';
import styles from './RecurrencePicker.module.scss';

const DAY_LABELS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

interface Props {
  kind: RecurrenceKind;
  time: string;
  daysOfWeek: number[];
  dayOfMonth: number;
  onKindChange: (kind: RecurrenceKind) => void;
  onTimeChange: (time: string) => void;
  onDaysOfWeekChange: (days: number[]) => void;
  onDayOfMonthChange: (day: number) => void;
}

export const RecurrencePicker: React.FC<Props> = ({
  kind,
  time,
  daysOfWeek,
  dayOfMonth,
  onKindChange,
  onTimeChange,
  onDaysOfWeekChange,
  onDayOfMonthChange,
}) => {
  const toggleDay = (day: number) => {
    if (daysOfWeek.includes(day)) {
      if (daysOfWeek.length > 1) {
        onDaysOfWeekChange(daysOfWeek.filter((d) => d !== day));
      }
    } else {
      onDaysOfWeekChange([...daysOfWeek, day].sort());
    }
  };

  return (
    <div className={styles.wrapper}>
      <div className={styles.tabs}>
        {(['daily', 'weekly', 'monthly'] as RecurrenceKind[]).map((k) => (
          <button
            key={k}
            type="button"
            className={`${styles.tab} ${kind === k ? styles.tabActive : ''}`}
            onClick={() => onKindChange(k)}
          >
            {k.charAt(0).toUpperCase() + k.slice(1)}
          </button>
        ))}
      </div>

      <div className={styles.fieldGroup}>
        {/* A native time input renders in the browser's locale (AM/PM for many
            users), so the label must not promise a 24-hour clock (#958). */}
        <label className={styles.label}>Time</label>
        <input
          type="time"
          className={styles.input}
          value={time}
          onChange={(e) => onTimeChange(e.target.value)}
        />
      </div>

      {kind === 'weekly' && (
        <div className={styles.fieldGroup}>
          <label className={styles.label}>Days of week</label>
          <div className={styles.dayPicker}>
            {DAY_LABELS.map((label, idx) => (
              <button
                key={idx}
                type="button"
                className={`${styles.dayBtn} ${daysOfWeek.includes(idx) ? styles.dayBtnActive : ''}`}
                onClick={() => toggleDay(idx)}
              >
                {label}
              </button>
            ))}
          </div>
        </div>
      )}

      {kind === 'monthly' && (
        <div className={styles.fieldGroup}>
          <label className={styles.label}>Day of month (1–28)</label>
          <input
            type="number"
            className={styles.input}
            min={1}
            max={28}
            value={dayOfMonth}
            onChange={(e) => onDayOfMonthChange(Math.min(28, Math.max(1, parseInt(e.target.value) || 1)))}
          />
        </div>
      )}
    </div>
  );
};
